/**
 * Permission system types and constants
 * These should match the permission codenames from the backend
 */

/**
 * Enum of all available permissions in the system
 * Use these constants instead of string literals for type safety
 */
export enum Permission {
  // Sales Permissions
  SALES_ACTIVITY = "rw_salesactivity",
  SALES_ACCOUNT = "rw_salesaccount",
  SALES_CUSTOMER = "rw_salescustomer",
  SALES_CAMPAIGN = "rw_salescampaign",
  SALES_PRODUCT = "rw_salesproduct",
  SALES_AUTOMATION = "rw_salesautomation",
  SALES_AUTOMATION_EXEC = "rw_salesautomationexec",
  OPPORTUNITY = "rw_opportunity",
  QUOTA = "rw_quota",
  SALES_LEAD = "rw_saleslead",
  LEAD_PIPELINE = "rw_leadpipeline",
  LEAD_STAGE = "rw_leadstage",
  SUPPORT_CASE_VIEW = "ro_aiemployee",
  SUPPORT_CASE_MANAGE = "rw_aiemployee",

  // Agent & Tools Permissions
  TRIAGE_SUB_AGENT = "rw_triagesubagent",
  AGENT_TOOLS = "rw_agent_tools",
  AGENT_PLATFORM_ADMIN = "rw_agentplatform_admin",
  AGENT_PLATFORM_RUNTIME = "rw_agentplatform_runtime",

  // System & Admin Permissions
  GROUP = "rw_group",
  PERMISSION = "rw_permission",
  TENANT = "rw_tenant",
  OPS_CONSOLE = "rw_ops_console",
  AUDIT = "rw_audit",
  APP_INTEGRATIONS = "rw_app_integrations",
  CRM_CHANGE_HISTORY = "rw_crmchangehistory",
  KNOWLEDGE_PACK_VIEW = "rw_knowledgepack_view",
  KNOWLEDGE_PACK_MANAGE = "rw_knowledgepack_manage",
  KNOWLEDGE_PACK_EXPORT = "rw_knowledgepack_export",
  KNOWLEDGE_PACK_APPROVE = "rw_knowledgepack_approve",
  TASKS = "rw_tasks",

  // Company OS permissions
  COMPANY_OS_USER = "rw_companyos_user",
  COMPANY_OS_MANAGER = "rw_companyos_manager",
  COMPANY_OS_ADMIN = "rw_companyos_admin",

  // User Permissions
  PROFILE = "rw_profile",
  MAKE_A_CALL = "rw_make_a_call"
}

/**
 * Type for permission values (the actual string values)
 */
export type PermissionValue = `${Permission}`;

/**
 * Type for an array of permissions
 */
export type PermissionArray = Permission[] | PermissionValue[];

/**
 * Permission groups for common use cases
 */
export const PermissionGroups = {
  // All sales-related permissions
  SALES: [
    Permission.SALES_ACTIVITY,
    Permission.SALES_ACCOUNT,
    Permission.SALES_CUSTOMER,
    Permission.SALES_CAMPAIGN,
    Permission.SALES_PRODUCT,
    Permission.OPPORTUNITY,
    Permission.QUOTA,
    Permission.SALES_LEAD,
    Permission.LEAD_PIPELINE,
    Permission.LEAD_STAGE
  ] as const,

  // All CRM/Data permissions
  CRM_DATA: [
    Permission.SALES_CUSTOMER,
    Permission.SALES_ACCOUNT,
    Permission.SALES_CAMPAIGN,
    Permission.SALES_PRODUCT,
    Permission.OPPORTUNITY,
    Permission.SALES_LEAD
  ] as const,

  // All agent-related permissions
  AGENT: [Permission.TRIAGE_SUB_AGENT, Permission.AGENT_TOOLS, Permission.AGENT_PLATFORM_ADMIN] as const,

  // All admin permissions
  ADMIN: [
    Permission.GROUP,
    Permission.PERMISSION,
    Permission.TENANT,
    Permission.AUDIT,
    Permission.APP_INTEGRATIONS,
    Permission.AGENT_PLATFORM_ADMIN
  ] as const,

  // Ops console permissions
  OPS: [Permission.OPS_CONSOLE] as const,

  // Knowledge pack permissions
  KNOWLEDGE_PACKS: [
    Permission.KNOWLEDGE_PACK_VIEW,
    Permission.KNOWLEDGE_PACK_MANAGE,
    Permission.KNOWLEDGE_PACK_EXPORT,
    Permission.KNOWLEDGE_PACK_APPROVE
  ] as const,

  // System access control permissions
  ACCESS_CONTROL: [Permission.GROUP, Permission.PERMISSION] as const,

  COMPANY_OS: [Permission.COMPANY_OS_USER, Permission.COMPANY_OS_MANAGER, Permission.COMPANY_OS_ADMIN] as const,
  COMPANY_OS_MANAGEMENT: [Permission.COMPANY_OS_MANAGER, Permission.COMPANY_OS_ADMIN] as const,
  COMPANY_OS_ADMINISTRATION: [Permission.COMPANY_OS_ADMIN] as const
} as const;

// Equipment currently shares the backend AI employee permission boundary.
// Keep the UI access contract domain-specific until dedicated backend codenames land.
export const EQUIPMENT_PERMISSIONS = {
  view: Permission.SUPPORT_CASE_VIEW,
  manage: Permission.SUPPORT_CASE_MANAGE
} as const;

/**
 * Helper to get all permission values as an array
 */
export const getAllPermissions = (): Permission[] => {
  return Object.values(Permission);
};

/**
 * Helper to check if a string is a valid permission
 */
export const isValidPermission = (value: string): value is Permission => {
  return Object.values(Permission).includes(value as Permission);
};
